import React, { useState } from 'react';
import type { ProductEndpoint } from '../../types';
import { SchemaEditor } from './SchemaEditor';
import { X, Plus } from 'lucide-react';

interface OpenAPIVisualBuilderProps {
  parsedSpec: any;
  specString: string;
  endpoints: ProductEndpoint[];
  onChange: (updatedSpec: any) => void;
  onStringChange: (updatedString: string) => void;
}

export const OpenAPIVisualBuilder: React.FC<OpenAPIVisualBuilderProps> = ({
  parsedSpec,
  specString,
  endpoints,
  onChange,
  onStringChange,
}) => {
  const [activeTab, setActiveTab] = useState<'info' | 'servers' | 'endpoints' | 'schemas'>('info');
  const [selectedEndpointId, setSelectedEndpointId] = useState<string>('');

  const updateSpec = (path: string[], value: any) => {
    // Deep clone to avoid mutating state directly
    const newSpec = JSON.parse(
      JSON.stringify(parsedSpec || { openapi: '3.0.0', info: { title: '', version: '' } })
    );

    let current = newSpec;
    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }

    current[path[path.length - 1]] = value;
    onChange(newSpec);
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
      <div className="border-b border-slate-200 dark:border-slate-800 flex px-4 space-x-6 bg-slate-50 dark:bg-slate-800/60 rounded-t-xl">
        {[
          { id: 'info', label: 'Informação Geral' },
          { id: 'servers', label: 'Servidores' },
          { id: 'endpoints', label: 'Endpoints' },
          { id: 'schemas', label: 'Esquemas (Modelos)' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`py-3 text-sm font-medium border-b-2 ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:border-slate-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {activeTab === 'info' && (
          <div className="space-y-4 max-w-2xl">
            <div>
              <label className="label">Título da API</label>
              <input
                type="text"
                className="input w-full"
                value={parsedSpec?.info?.title || ''}
                onChange={(e) => updateSpec(['info', 'title'], e.target.value)}
                placeholder="Ex: API de Pagamentos"
              />
            </div>
            <div>
              <label className="label">Versão</label>
              <input
                type="text"
                className="input w-full"
                value={parsedSpec?.info?.version || ''}
                onChange={(e) => updateSpec(['info', 'version'], e.target.value)}
                placeholder="Ex: 1.0.0"
              />
            </div>
            <div>
              <label className="label">Descrição</label>
              <textarea
                className="input w-full h-32"
                value={parsedSpec?.info?.description || ''}
                onChange={(e) => updateSpec(['info', 'description'], e.target.value)}
                placeholder="Descrição detalhada da API..."
              />
            </div>
          </div>
        )}

        {activeTab === 'servers' && (
          <div className="space-y-4 max-w-3xl">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-slate-900 dark:text-white">Servidores</h3>
              <button
                className="btn-secondary text-sm py-1 inline-flex items-center gap-1"
                onClick={() => {
                  const servers = [...(parsedSpec?.servers || [])];
                  servers.push({ url: '', description: '' });
                  updateSpec(['servers'], servers);
                }}
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar Servidor</span>
              </button>
            </div>

            {(parsedSpec?.servers || []).length === 0 && (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-dashed border-slate-300 dark:border-slate-700">
                Nenhum servidor configurado.
              </div>
            )}

            {(parsedSpec?.servers || []).map((server: any, idx: number) => (
              <div
                key={idx}
                className="flex space-x-3 items-start bg-slate-50 dark:bg-slate-800/50 p-4 rounded border border-slate-200 dark:border-slate-700"
              >
                <div className="flex-1 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      URL
                    </label>
                    <input
                      className="input w-full mt-1"
                      placeholder="Ex: https://api.sandbox.com/v1"
                      value={server.url || ''}
                      onChange={(e) => {
                        const servers = [...(parsedSpec?.servers || [])];
                        servers[idx].url = e.target.value;
                        updateSpec(['servers'], servers);
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      Descrição
                    </label>
                    <input
                      className="input w-full mt-1"
                      placeholder="Ex: Ambiente de Sandbox"
                      value={server.description || ''}
                      onChange={(e) => {
                        const servers = [...(parsedSpec?.servers || [])];
                        servers[idx].description = e.target.value;
                        updateSpec(['servers'], servers);
                      }}
                    />
                  </div>
                </div>
                <button
                  className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded"
                  title="Remover Servidor"
                  onClick={() => {
                    const servers = [...(parsedSpec?.servers || [])];
                    servers.splice(idx, 1);
                    updateSpec(['servers'], servers);
                  }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'endpoints' && (
          <div className="space-y-6">
            <div>
              <label className="label">Selecione um Endpoint para documentar</label>
              <select
                className="select"
                value={selectedEndpointId}
                onChange={(e) => setSelectedEndpointId(e.target.value)}
              >
                <option value="" disabled>
                  Selecione um endpoint criado na aba Endpoints...
                </option>
                {endpoints.map((ep) => (
                  <option key={ep.id} value={ep.id}>
                    {ep.public_method} {ep.public_path} - {ep.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedEndpointId &&
              (() => {
                const ep = endpoints.find((e) => e.id === selectedEndpointId);
                if (!ep) return null;

                const publicPath = ep.public_path;
                const externalPath = ep.external_path || '';
                const method = ep.public_method.toLowerCase();

                // Find the best matching path in the spec
                let matchedPath = publicPath;
                const specPaths = Object.keys(parsedSpec?.paths || {});

                if (parsedSpec?.paths?.[publicPath]?.[method]) {
                  matchedPath = publicPath;
                } else if (externalPath && parsedSpec?.paths?.[externalPath]?.[method]) {
                  matchedPath = externalPath;
                } else {
                  // Suffix matching (e.g. spec has "/consultar", ep has "/v1/rupe/consultar")
                  const suffixMatch = specPaths.find(
                    (p) =>
                      p.length > 1 &&
                      (publicPath.endsWith(p) || (externalPath && externalPath.endsWith(p)))
                  );
                  if (suffixMatch && parsedSpec?.paths?.[suffixMatch]?.[method]) {
                    matchedPath = suffixMatch;
                  }
                }

                const path = matchedPath;
                const methodSpec = parsedSpec?.paths?.[path]?.[method] || {};

                return (
                  <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg space-y-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <span className="bg-blue-100 text-blue-800 font-bold px-2 py-1 rounded text-sm uppercase">
                        {method}
                      </span>
                      <span className="font-mono text-gray-700">{path}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Sumário</label>
                        <input
                          className="input w-full"
                          value={methodSpec.summary || ''}
                          onChange={(e) =>
                            updateSpec(['paths', path, method, 'summary'], e.target.value)
                          }
                          placeholder="Ex: Consultar Cliente"
                        />
                      </div>
                      <div>
                        <label className="label">Operation ID (Opcional)</label>
                        <input
                          className="input w-full"
                          value={methodSpec.operationId || ''}
                          onChange={(e) =>
                            updateSpec(['paths', path, method, 'operationId'], e.target.value)
                          }
                          placeholder="Ex: getClient"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="label">Descrição Completa</label>
                      <textarea
                        className="input w-full h-24"
                        value={methodSpec.description || ''}
                        onChange={(e) =>
                          updateSpec(['paths', path, method, 'description'], e.target.value)
                        }
                      />
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">
                        Parâmetros (Query, Path, Header)
                      </h4>
                      <button
                        className="btn-secondary text-sm py-1 mb-4"
                        onClick={() => {
                          const parameters = [...(methodSpec.parameters || [])];
                          parameters.push({
                            name: '',
                            in: 'query',
                            required: false,
                            schema: { type: 'string' },
                          });
                          updateSpec(['paths', path, method, 'parameters'], parameters);
                        }}
                      >
                        + Adicionar Parâmetro
                      </button>
                      <div className="space-y-3">
                        {(methodSpec.parameters || []).map((param: any, idx: number) => (
                          <div
                            key={idx}
                            className="flex space-x-3 items-center bg-white p-3 border border-gray-200 rounded"
                          >
                            <input
                              className="input w-1/3"
                              placeholder="Nome (ex: id)"
                              value={param.name || ''}
                              onChange={(e) => {
                                const parameters = [...(methodSpec.parameters || [])];
                                parameters[idx].name = e.target.value;
                                updateSpec(['paths', path, method, 'parameters'], parameters);
                              }}
                            />
                            <select
                              className="select w-1/4"
                              value={param.in || 'query'}
                              onChange={(e) => {
                                const parameters = [...(methodSpec.parameters || [])];
                                parameters[idx].in = e.target.value;
                                updateSpec(['paths', path, method, 'parameters'], parameters);
                              }}
                            >
                              <option value="query">Query</option>
                              <option value="path">Path</option>
                              <option value="header">Header</option>
                            </select>
                            <label className="flex items-center space-x-2 text-sm text-gray-700 w-1/4">
                              <input
                                type="checkbox"
                                checked={!!param.required}
                                onChange={(e) => {
                                  const parameters = [...(methodSpec.parameters || [])];
                                  parameters[idx].required = e.target.checked;
                                  updateSpec(['paths', path, method, 'parameters'], parameters);
                                }}
                              />
                              <span>Obrigatório</span>
                            </label>
                            <button
                              className="text-red-500 hover:text-red-700 px-2"
                              onClick={() => {
                                const parameters = [...(methodSpec.parameters || [])];
                                parameters.splice(idx, 1);
                                updateSpec(['paths', path, method, 'parameters'], parameters);
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">
                        Corpo do Pedido (Request Body)
                      </h4>
                      <div className="bg-white p-4 border border-gray-200 rounded space-y-4">
                        <label className="flex items-center space-x-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={!!methodSpec?.requestBody?.required}
                            onChange={(e) => {
                              const reqBody = {
                                ...(methodSpec.requestBody || {
                                  content: { 'application/json': { schema: {} } },
                                }),
                              };
                              reqBody.required = e.target.checked;
                              updateSpec(['paths', path, method, 'requestBody'], reqBody);
                            }}
                          />
                          <span>Corpo do pedido é obrigatório</span>
                        </label>

                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">
                            Modelo de Dados (Schema)
                          </label>
                          {(() => {
                            const currentSchema =
                              methodSpec?.requestBody?.content?.['application/json']?.schema || {};
                            const currentRef = currentSchema.$ref || '';
                            const availableSchemas = Object.keys(
                              parsedSpec?.components?.schemas || {}
                            );

                            return (
                              <div className="space-y-3">
                                <select
                                  className="select w-full"
                                  value={
                                    currentRef.replace('#/components/schemas/', '') || 'custom'
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const reqBody = { ...(methodSpec.requestBody || {}) };
                                    if (!reqBody.content) reqBody.content = {};
                                    if (!reqBody.content['application/json'])
                                      reqBody.content['application/json'] = {};

                                    if (val === 'custom') {
                                      // Remove $ref, switch to inline schema object
                                      reqBody.content['application/json'].schema = {
                                        type: 'object',
                                        properties: {},
                                      };
                                    } else {
                                      // Set $ref
                                      reqBody.content['application/json'].schema = {
                                        $ref: `#/components/schemas/${val}`,
                                      };
                                    }

                                    updateSpec(['paths', path, method, 'requestBody'], reqBody);
                                  }}
                                >
                                  <option value="custom">Objeto Customizado (Inline JSON)</option>
                                  <optgroup label="Modelos Existentes (Aba Esquemas)">
                                    {availableSchemas.map((s) => (
                                      <option key={s} value={s}>
                                        {s}
                                      </option>
                                    ))}
                                  </optgroup>
                                </select>

                                {(!currentRef || currentRef === '') && (
                                  <textarea
                                    className="input w-full font-mono text-xs h-32"
                                    placeholder='Ex: { "type": "object", "properties": { ... } }'
                                    value={JSON.stringify(currentSchema, null, 2)}
                                    onChange={(e) => {
                                      try {
                                        const parsedSchema = JSON.parse(e.target.value);
                                        const reqBody = { ...(methodSpec.requestBody || {}) };
                                        reqBody.content['application/json'].schema = parsedSchema;
                                        updateSpec(['paths', path, method, 'requestBody'], reqBody);
                                      } catch (err) {
                                        // Ignore invalid JSON while typing
                                      }
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })()}
                          <p className="text-xs text-gray-500 mt-2">
                            Dica: Crie os seus modelos na aba "Esquemas" para os poder selecionar
                            facilmente no dropdown acima, tal como o Swagger faz internamente.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4">
                        Respostas (Responses)
                      </h4>
                      <button
                        className="btn-secondary text-sm py-1 mb-4"
                        onClick={() => {
                          const responses = { ...(methodSpec.responses || {}) };
                          if (!responses['200']) {
                            responses['200'] = { description: 'Sucesso' };
                            updateSpec(['paths', path, method, 'responses'], responses);
                          }
                        }}
                      >
                        + Adicionar Resposta 200
                      </button>
                      <div className="space-y-4">
                        {Object.entries(methodSpec.responses || {}).map(
                          ([code, response]: [string, any]) => (
                            <div key={code} className="bg-white p-4 border border-gray-200 rounded">
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-bold text-gray-800">Status {code}</span>
                                <button
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => {
                                    const responses = { ...(methodSpec.responses || {}) };
                                    delete responses[code];
                                    updateSpec(['paths', path, method, 'responses'], responses);
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                              <div>
                                <label className="text-xs text-gray-600">Descrição</label>
                                <input
                                  className="input w-full mt-1"
                                  value={response.description || ''}
                                  onChange={(e) => {
                                    const responses = { ...(methodSpec.responses || {}) };
                                    responses[code].description = e.target.value;
                                    updateSpec(['paths', path, method, 'responses'], responses);
                                  }}
                                />
                                <label className="text-xs text-gray-600 mt-2 block">
                                  Modelo de Dados da Resposta (Schema)
                                </label>
                                {(() => {
                                  const currentSchema =
                                    response?.content?.['application/json']?.schema || {};
                                  const currentRef = currentSchema.$ref || '';
                                  const availableSchemas = Object.keys(
                                    parsedSpec?.components?.schemas || {}
                                  );

                                  return (
                                    <div className="space-y-3 mt-1">
                                      <select
                                        className="select w-full"
                                        value={
                                          currentRef.replace('#/components/schemas/', '') ||
                                          'custom'
                                        }
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          const responses = { ...(methodSpec.responses || {}) };
                                          if (!responses[code].content)
                                            responses[code].content = {};
                                          if (!responses[code].content['application/json'])
                                            responses[code].content['application/json'] = {};

                                          if (val === 'custom') {
                                            responses[code].content['application/json'].schema = {
                                              type: 'object',
                                              properties: {},
                                            };
                                          } else {
                                            responses[code].content['application/json'].schema = {
                                              $ref: `#/components/schemas/${val}`,
                                            };
                                          }

                                          updateSpec(
                                            ['paths', path, method, 'responses'],
                                            responses
                                          );
                                        }}
                                      >
                                        <option value="custom">
                                          Objeto Customizado (Inline JSON)
                                        </option>
                                        <optgroup label="Modelos Existentes (Aba Esquemas)">
                                          {availableSchemas.map((s) => (
                                            <option key={s} value={s}>
                                              {s}
                                            </option>
                                          ))}
                                        </optgroup>
                                      </select>

                                      {(!currentRef || currentRef === '') && (
                                        <textarea
                                          className="input w-full font-mono text-xs h-24"
                                          placeholder='Ex: { "type": "object", "properties": { ... } }'
                                          value={JSON.stringify(currentSchema, null, 2)}
                                          onChange={(e) => {
                                            try {
                                              const parsedSchema = JSON.parse(e.target.value);
                                              const responses = { ...(methodSpec.responses || {}) };
                                              responses[code].content['application/json'].schema =
                                                parsedSchema;
                                              updateSpec(
                                                ['paths', path, method, 'responses'],
                                                responses
                                              );
                                            } catch (err) {
                                              // Ignore invalid JSON while typing
                                            }
                                          }}
                                        />
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
          </div>
        )}

        {activeTab === 'schemas' && (
          <div className="bg-white dark:bg-slate-900">
            <div className="mb-4 text-sm bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-300 p-3 rounded-xl border border-blue-200 dark:border-blue-800/50">
              Crie os seus modelos de dados aqui para depois os associar aos Corpos de Pedido
              (Request Body) e Respostas na aba Endpoints.
            </div>
            <SchemaEditor currentSpec={specString} onSave={onStringChange} isSaving={false} />
          </div>
        )}
      </div>
    </div>
  );
};
